<template>
  <div>
    <!-- Settings Tab Navigation -->
    <div class="settings-tabs-wrap">
      <div class="settings-tabs" ref="tabsEl">

       

        <router-link
          v-if="permission.settings_module == 1 && loggedInUser.is_super == 1"
          :to="$url('/manage-store')"
          class="tab-item"
          :class="{ 'tab-item--active': isRoute(['Manage Store', 'Create Store', 'Edit Store']) }"
        >
          <font-awesome-icon icon="building" class="tab-icon" />
          Manage Stores
        </router-link>

        <!-- Inventory Category dropdown -->
        <div
          v-if="permission.view_inventory_category == 1 || permission.view_inventory_subcategory == 1"
          class="tab-dropdown"
          :class="{ 'tab-item--active': isRoute(['Inventory Category', 'Inventory Subcategory']) }"
          ref="categoryDrop"
        >
          <button class="tab-item tab-dropdown-trigger" @click="toggleDrop('category', $event)">
            <font-awesome-icon icon="tags" class="tab-icon" />
            Inventory Category
            <font-awesome-icon icon="chevron-down" class="drop-chevron" :class="{ rotated: categoryOpen }" />
          </button>
        </div>

        <!-- ── Teleported dropdowns (escape overflow:hidden) ── -->
        <teleport to="body">

          <!-- Inventory Category menu -->
          <div
            v-if="categoryOpen"
            class="floating-drop-menu"
            :style="categoryMenuStyle"
            ref="categoryMenu"
          >
            <router-link
              v-if="permission.view_inventory_category == 1"
              :to="$url('/inventory-category')"
              class="tab-drop-item"
              @click="categoryOpen = false"
            >
              <font-awesome-icon icon="tag" class="me-2" /> Category
            </router-link>
            <router-link
              v-if="permission.view_inventory_subcategory == 1"
              :to="$url('/inventory-subcategory')"
              class="tab-drop-item"
              @click="categoryOpen = false"
            >
              <font-awesome-icon icon="layer-group" class="me-2" /> Subcategory
            </router-link>
          </div>

        </teleport>
        <router-link
          v-if="permission.view_cutomers == 1"
          :to="$url('/all-customers')"
          class="tab-item"
          :class="{ 'tab-item--active': isRoute(['Create Customer', 'Bulk Customer Upload', 'Edit Customer']) }"
        >
          <font-awesome-icon icon="users" class="tab-icon" />
          Customers
        </router-link>

        <!-- Suppliers -->
        <router-link
          v-if="permission.view_suppliers == 1"
          :to="$url('/all-suppliers')"
          class="tab-item"
          :class="{ 'tab-item--active': isRoute(['Create Supplier', 'Bulk Supplier Upload', 'Edit Supplier']) }"
        >
          <font-awesome-icon icon="truck" class="tab-icon" />
          Suppliers
        </router-link>

        <!-- Payment Type -->
        <template v-if="permission.view_payment_type == 1 || permission.change_payment_type == 1">
          <router-link
            v-if="!freemium"
            :to="$url('/all-payments')"
            class="tab-item"
          >
            <font-awesome-icon icon="money-bill-wave" class="tab-icon" />
            Payment Type
          </router-link>
          <button v-else class="tab-item tab-item--locked" @click="freemiumPrompt">
            <font-awesome-icon icon="lock" class="tab-icon tab-icon--lock" />
            Payment Type
          </button>
        </template>

        <!-- Tax -->
        <template v-if="permission.view_taxes == 1">
          <router-link v-if="!freemium" :to="$url('/tax')" class="tab-item">
            <font-awesome-icon icon="receipt" class="tab-icon" />
            Tax
          </router-link>
          <button v-else class="tab-item tab-item--locked" @click="freemiumPrompt">
            <font-awesome-icon icon="lock" class="tab-icon tab-icon--lock" />
            Tax
          </button>
        </template>

        <!-- Notification -->
        <template v-if="permission.change_notification == 1">
          <router-link v-if="!freemium" :to="$url('/notifications')" class="tab-item">
            <font-awesome-icon icon="bell" class="tab-icon" />
            Notification
          </router-link>
          <button v-else class="tab-item tab-item--locked" @click="freemiumPrompt">
            <font-awesome-icon icon="lock" class="tab-icon tab-icon--lock" />
            Notification
          </button>
        </template>

        <!-- Scroll fade indicators -->
        <div class="scroll-fade scroll-fade--left"  :class="{ visible: canScrollLeft }" />
        <div class="scroll-fade scroll-fade--right" :class="{ visible: canScrollRight }" />

      </div>
    </div>

    

    <!-- Backdrop -->
    <div v-if="password_active || email_active" class="modal-backdrop-custom" />

  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

export default {
  name: 'SettingsHeaderTab',

  setup() {
    const router = useRouter();
    const toast  = useToast();
    const store  = useStore();

    // ── Navigation state ──────────────────────────────────────────────
    const tabsEl       = ref(null);
    const accountDrop  = ref(null);
    const categoryDrop = ref(null);
    const accountMenu  = ref(null);
    const categoryMenu = ref(null);
    const accountOpen  = ref(false);
    const categoryOpen = ref(false);
    const canScrollLeft  = ref(false);
    const canScrollRight = ref(false);

    // Position of floating menus (set when trigger is clicked)
    const accountMenuStyle  = ref({});
    const categoryMenuStyle = ref({});

    const getMenuStyle = (triggerEl) => {
      const rect = triggerEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const menuH = 120; // approx menu height
      const showAbove = spaceBelow < menuH + 8;
      return {
        position:  'fixed',
        left:      `${rect.left}px`,
        minWidth:  `${Math.max(rect.width, 180)}px`,
        zIndex:    9999,
        ...(showAbove
          ? { bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' }
          : { top: `${rect.bottom + 4}px`, bottom: 'auto' }),
      };
    };

    const isRoute = (names) => names.includes(router.currentRoute.value.name);

    const updateScrollState = () => {
      const el = tabsEl.value;
      if (!el) return;
      canScrollLeft.value  = el.scrollLeft > 4;
      canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    };

    // Toggle with position calculation
    const toggleDrop = (which, evt) => {
      const btn = evt.currentTarget;
      if (which === 'account') {
        categoryOpen.value = false;
        accountOpen.value  = !accountOpen.value;
        if (accountOpen.value) accountMenuStyle.value = getMenuStyle(btn);
      } else {
        accountOpen.value  = false;
        categoryOpen.value = !categoryOpen.value;
        if (categoryOpen.value) categoryMenuStyle.value = getMenuStyle(btn);
      }
    };

    // Close dropdowns on outside click
    const handleOutsideClick = (e) => {
      const inAccount  = accountDrop.value?.contains(e.target)  || accountMenu.value?.contains(e.target);
      const inCategory = categoryDrop.value?.contains(e.target) || categoryMenu.value?.contains(e.target);
      if (!inAccount)  accountOpen.value  = false;
      if (!inCategory) categoryOpen.value = false;
    };

    // ── Freemium ──────────────────────────────────────────────────────
    const freemium = ref(true);
    const freemiumPrompt = () => toast.error('Subscribe or upgrade to use this feature.');

    const activeSubscription = () => {
      axios.get('/subscription/user')
        .then(({ data }) => {
          freemium.value = !data.data.some(d => d.status === 1 || d.status === 0);
        })
        .catch(() => {});
    };

    
    // Flatten validation errors (string, array, or object) into strings
    const flatErrors = (errors) => {
      if (!errors) return [];
      // Plain string — wrap in array so it renders as one item
      if (typeof errors === 'string') return errors ? [errors] : [];
      // Flat array of strings e.g. ['Wrong password']
      if (Array.isArray(errors)) {
        return errors.flatMap(e => (typeof e === 'string' ? [e] : Array.isArray(e) ? e : Object.values(e).flat()));
      }
      // Object of field arrays e.g. { password: ['Wrong password'] }
      return Object.values(errors).flat();
    };

    // ── Permissions & user ────────────────────────────────────────────
    const permission   = computed(() => store.state.getCurrentUserPermission);
    const loggedInUser = computed(() => store.state.getLoggedInUser);

    const getCurrentUser = () => {
      axios.get('/user').then(({ data }) => {
        store.dispatch('getCurrentUser', data.data.data);
      }).catch(() => {});
    };

    onMounted(async () => {
      getCurrentUser();
      activeSubscription();
      await nextTick();
      const el = tabsEl.value;
      if (el) {
        el.addEventListener('scroll', updateScrollState, { passive: true });
        updateScrollState();
      }
      document.addEventListener('click', handleOutsideClick);
    });

    return {
      tabsEl, categoryDrop, categoryMenu, categoryOpen, accountMenuStyle, categoryMenuStyle,
      canScrollLeft, canScrollRight,
      isRoute, updateScrollState, toggleDrop,
      freemium, freemiumPrompt, flatErrors,
      permission, loggedInUser,
    };
  },
};
</script>

<style scoped>
/* ── Tabs wrap ───────────────────────────────────────────────────────── */
.settings-tabs-wrap { position:relative; width:100%; background:#fff; border-bottom:1px solid #e4eaf2; }
.settings-tabs      { display:flex; align-items:center; gap:2px; overflow-x:auto; overflow-y:visible; scroll-behavior:smooth; -webkit-overflow-scrolling:touch; padding:10px 16px; scrollbar-width:none; }
.settings-tabs::-webkit-scrollbar { display:none; }

/* ── Tab item base ───────────────────────────────────────────────────── */
.tab-item { display:inline-flex; align-items:center; gap:7px; white-space:nowrap; padding:7px 14px; border-radius:8px; font-size:13px; font-weight:500; color:#7a90b0; text-decoration:none; border:1px solid transparent; background:transparent; cursor:pointer; transition:all .15s; flex-shrink:0; line-height:1; }
.tab-item:hover:not(.tab-item--locked) { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
.tab-item--active,
.router-link-active.tab-item { background:rgba(0,74,173,.08); color:#004AAD; font-weight:700; border-color:rgba(0,74,173,.18); }
.tab-item--locked  { color:#b0bdd0; cursor:not-allowed; }
.tab-item--locked:hover { background:#fdf4f4; color:#c0a080; border-color:#f5e0c8; }
.tab-icon          { font-size:12px; flex-shrink:0; }
.tab-icon--lock    { font-size:11px; color:#c0a080; }

/* ── Dropdown tabs ───────────────────────────────────────────────────── */
.tab-dropdown         { position:relative; }
.tab-dropdown-trigger { }
.drop-chevron         { font-size:10px; color:#7a90b0; transition:transform .2s; }
.drop-chevron.rotated { transform:rotate(180deg); }

/* ── Floating dropdown menu (teleported to body) ─────────────────────── */
.floating-drop-menu   { background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.12); padding:6px; z-index:9999; }
.tab-drop-item        { display:flex; align-items:center; width:100%; padding:9px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; border:none; background:transparent; text-decoration:none; transition:background .12s; text-align:left; white-space:nowrap; }
.tab-drop-item:hover  { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
.tab-drop-item--locked { color:#b0bdd0; cursor:not-allowed; }
.tab-drop-item--locked:hover { background:#fdf4f4; color:#c0a080; }
.me-2 { margin-right:8px; }

/* ── Scroll fades ────────────────────────────────────────────────────── */
.scroll-fade          { position:absolute; top:0; bottom:0; width:48px; pointer-events:none; opacity:0; transition:opacity .2s; z-index:2; }
.scroll-fade.visible  { opacity:1; }
.scroll-fade--left    { left:0; background:linear-gradient(to right, rgba(255,255,255,.95), transparent); }
.scroll-fade--right   { right:0; background:linear-gradient(to left, rgba(255,255,255,.95), transparent); }

/* ── Modal overlay ───────────────────────────────────────────────────── */
.modal-overlay    { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }

/* ── Modal box ───────────────────────────────────────────────────────── */
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:400px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }

.modal-header-styled { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
.modal-title-styled  { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close-btn     { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-body-styled   { padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
.modal-footer-styled { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }

/* ── Error box ───────────────────────────────────────────────────────── */
.error-box  { background:#fff5f5; border-left:3px solid #e03535; margin:0 24px; border-radius:0 6px 6px 0; padding:12px 14px; }
.error-item { font-size:12px; color:#c02828; margin-bottom:4px; display:flex; align-items:center; }
.error-item:last-child { margin-bottom:0; }
.me-1 { margin-right:4px; }

/* ── Form ────────────────────────────────────────────────────────────── */
.form-group-styled { display:flex; flex-direction:column; gap:6px; }
.form-label-styled { font-size:13px; font-weight:600; color:#1a2b45; }
.form-input-styled { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; }
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }

/* ── Password field ──────────────────────────────────────────────────── */
.pw-wrap   { position:relative; display:flex; align-items:center; }
.pw-wrap .form-input-styled { padding-right:40px; }
.pw-toggle { position:absolute; right:10px; border:none; background:transparent; color:#7a90b0; cursor:pointer; font-size:14px; padding:0; transition:color .15s; }
.pw-toggle:hover { color:#1a2b45; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; border:none; }
.btn-ghost:hover { background:#e4eaf2; }
</style>